import axios, { AxiosHeaders, AxiosRequestConfig } from "axios";
import { SERVER_URL } from "../config/env";
import { buildAxiosHeaders } from "../utils/authHeaders";
import { tokenService } from "../services/tokenService";

export const apiClient = axios.create({
  baseURL: SERVER_URL,
  withCredentials: true,
});

type AuthHeaderOptions = {
  includeChatToken?: boolean;
};

export const withAuthHeaders = (
  config: AxiosRequestConfig = {},
  options?: AuthHeaderOptions
) => {
  const headers = buildAxiosHeaders(
    (config.headers as Record<string, string> | undefined) ?? undefined,
    options
  );

  return {
    ...config,
    headers: headers.headers,
  };
};

// request interceptor: proactive refresh then attach bearer (matches RTK baseQueryWithReauth)
apiClient.interceptors.request.use(async (config) => {
  const token = await tokenService.getValidAccessToken();

  if (token) {
    const headerBag = config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders(config.headers);
    headerBag.set("Authorization", `Bearer ${token}`);
    config.headers = headerBag;
  }

  return config;
});

// response interceptor: handle 401 errors with reactive refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest || error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    // prevent infinite retry loop
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // mark as retry to prevent loop
    originalRequest._retry = true;

    // reactive refresh: try to refresh the token silently
    console.log("[ApiClient] Got 401, attempting token refresh...");
    try {
      const tokens = await tokenService.refreshSilent();

      if (!tokens) {
        console.log("[ApiClient] Token refresh failed, request will fail");
        // session-expired event will be emitted by tokenService if refresh token is invalid
        return Promise.reject(error);
      }

      // retry with new token
      console.log("[ApiClient] Retrying request with new token...");
      const updatedToken = await tokenService.getValidAccessToken();
      if (updatedToken) {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${updatedToken}`,
        };
      }

      return apiClient(originalRequest);
    } catch (refreshError) {
      // if refresh itself throws an error, reject the original request
      console.error("[ApiClient] Token refresh threw an error:", refreshError);
      // session-expired event will be emitted by tokenService if refresh token is invalid
      return Promise.reject(error);
    }

  }
);
